import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  SurveyQuestion,
  SurveyResponse,
  SurveyRewardCampaignForCarousel,
} from "../../../../../../types/misc";
import { submitSurveyForReward } from "../../../../../../api/rest/authenticated/Survey";
import { useAppDispatch } from "../../../../../../redux/hooks";
import { loadRewards } from "../../../../../../redux/actions/rewards";
import GenericDialog from "../../../../../StoreComponents/StoreDialogs/GenericDialog";

interface ISurveyContext {
  numberOfQuestion: number;
  submitting: boolean;
  surveyQuestions: SurveyQuestion[];
  currentStep: number;
  receivedReward: number;
  question?: SurveyQuestion;
  setCurrentStep: (step: number) => void;
  isInFirstStep: boolean;
  isInLastStep: boolean;
  moveToNextStep: () => void;
  survey: SurveyRewardCampaignForCarousel;
  moveToPreviousStep: () => void;
  answers: Partial<SurveyResponse>[];
  setAnswers: React.Dispatch<React.SetStateAction<Partial<SurveyResponse>[]>>;
  timer: number[];
  setTimer: React.Dispatch<React.SetStateAction<number[]>>;
  hasValidationError: boolean;
}

// Create a context with a default value.
const SurveyContext = createContext<ISurveyContext>({} as ISurveyContext);

// Create a Provider component.
export const SurveyProvider = ({
  children,
  survey,
}: {
  children: React.ReactNode;
  survey: SurveyRewardCampaignForCarousel;
}) => {
  const surveyQuestions = useMemo(
    () => survey?.survey?.surveyQuestions ?? [],
    [survey]
  );
  const dispatch = useAppDispatch();
  const [answers, setAnswers] = useState<Partial<SurveyResponse>[]>(
    surveyQuestions.map(() => ({}))
  );
  const [submitting, setSubmitting] = useState(false);
  const [timer, setTimer] = useState<number[]>(surveyQuestions.map(() => 0));
  const [receivedReward, setReceivedReward] = useState<number>(0);
  const [hasValidationError, setHasValidationError] = useState(false);
  // Copied
  const [currentStep, setCurrentStep] = useState(-1);
  const isInFirstStep = currentStep === 0;
  const isInLastStep = currentStep + 1 >= surveyQuestions.length;

  const numberOfQuestion = surveyQuestions.length;

  const submit = useCallback(async () => {
    try {
      const surveyId = survey?.extendedAttributes.surveyId;
      const res = await submitSurveyForReward({
        campaignId: survey?.campaignId,
        surveyId,
        responses: answers as SurveyResponse[],
      });
      if (res.data) {
        if (res.data.success) {
          if (res.data.reward?.rewardAmount) {
            setReceivedReward(res.data.reward.rewardAmount);
            dispatch(loadRewards());
          }
          return true; // Return success
        } else if (res.data.valid === false) {
          // Show error dialog
          setHasValidationError(true);
          GenericDialog.show({
            title: "Invalid Survey Response",
            text: "Some of your answers don't meet the survey requirements. Please go back and review your responses to ensure they are complete and accurate.",
            actions: [
              {
                text: "Review Answers",
                variant: "solid",
                onClick: () => {
                  setHasValidationError(false);
                  // Keep the user on the last question
                  setCurrentStep(numberOfQuestion - 1);
                },
              },
            ],
          });
          return false; // Return failure
        }
      } else {
        GenericDialog.show({
          title: "Error Submitting Survey",
          text: "An error occurred while submitting your survey. Please try again.",
          actions: [
            {
              text: "OK",
              variant: "solid",
            },
          ],
        });
        return false;
      }
      return false;
    } catch (e) {
      // Go to error state here
      console.error(e);
      GenericDialog.show({
        title: "Error Submitting Survey",
        text: "An error occurred while submitting your survey. Please try again.",
        actions: [
          {
            text: "OK",
            variant: "solid",
          },
        ],
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    answers,
    dispatch,
    survey?.campaignId,
    survey?.extendedAttributes.surveyId,
    numberOfQuestion,
  ]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    const success = await submit();
    return success;
  }, [submit]);

  const moveToNextStep = useCallback(async () => {
    if (currentStep === numberOfQuestion - 1) {
      const success = await handleSubmit();
      if (!success) {
        // Don't increment step if submission failed
        return;
      }
    }

    setCurrentStep((prev) => prev + 1);
  }, [currentStep, numberOfQuestion, handleSubmit]);

  const moveToPreviousStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const question =
    currentStep === -1 ? undefined : surveyQuestions[currentStep];

  const value = useMemo(
    () => ({
      numberOfQuestion,
      receivedReward,
      surveyQuestions,
      currentStep,
      question,
      submitting,
      setCurrentStep,
      isInFirstStep,
      isInLastStep,
      moveToNextStep,
      moveToPreviousStep,
      survey,
      answers,
      setAnswers,
      timer,
      setTimer,
      hasValidationError,
    }),
    [
      answers,
      currentStep,
      isInFirstStep,
      isInLastStep,
      moveToNextStep,
      numberOfQuestion,
      question,
      receivedReward,
      submitting,
      survey,
      surveyQuestions,
      timer,
      hasValidationError,
    ]
  );

  // The value prop is where we provide the data that consuming components can subscribe to.
  return (
    <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
  );
};

// Custom hook to use the context
export const useSurvey = () => useContext(SurveyContext);
